import FormatAlignCenterRounded from '@mui/icons-material/FormatAlignCenterRounded';
import FormatAlignLeftRounded from '@mui/icons-material/FormatAlignLeftRounded';
import FormatAlignRightRounded from '@mui/icons-material/FormatAlignRightRounded';
import FormatBoldRounded from '@mui/icons-material/FormatBoldRounded';
import FormatClearRounded from '@mui/icons-material/FormatClearRounded';
import FormatColorTextRounded from '@mui/icons-material/FormatColorTextRounded';
import FormatItalicRounded from '@mui/icons-material/FormatItalicRounded';
import FormatListBulletedRounded from '@mui/icons-material/FormatListBulletedRounded';
import FormatListNumberedRounded from '@mui/icons-material/FormatListNumberedRounded';
import FormatStrikethroughRounded from '@mui/icons-material/FormatStrikethroughRounded';
import FormatUnderlinedRounded from '@mui/icons-material/FormatUnderlinedRounded';
import LinkRounded from '@mui/icons-material/LinkRounded';
import RedoRounded from '@mui/icons-material/RedoRounded';
import TableChartOutlined from '@mui/icons-material/TableChartOutlined';
import UndoRounded from '@mui/icons-material/UndoRounded';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormHelperText,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { TableKit } from '@tiptap/extension-table';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyleKit } from '@tiptap/extension-text-style';
import { EditorContent, useEditor, useEditorState, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { toEditorHtml } from '../lib/richText';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';

const SIZES = ['12px', '14px', '16px', '18px', '24px', '32px'];
const COLOURS = ['#1f2328', '#05473E', '#1565c0', '#2e7d32', '#ed6c02', '#d32f2f', '#6a1b9a'];

function Tool({
  title,
  active,
  disabled,
  onClick,
  children,
}: {
  title: string;
  active?: boolean;
  disabled?: boolean;
  onClick: (e: MouseEvent<HTMLElement>) => void;
  children: ReactNode;
}) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          aria-label={title}
          aria-pressed={active}
          disabled={disabled}
          onClick={onClick}
          sx={{
            borderRadius: 1,
            color: active ? 'primary.main' : 'text.secondary',
            bgcolor: active ? 'action.selected' : undefined,
          }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function MenuTool({
  title,
  icon,
  items,
}: {
  title: string;
  icon: ReactNode;
  items: { label: string; onClick: () => void; disabled?: boolean; preview?: ReactNode }[];
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <Tool title={title} onClick={(e) => setAnchor(e.currentTarget)}>
        {icon}
      </Tool>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {items.map((i) => (
          <MenuItem
            key={i.label}
            dense
            disabled={i.disabled}
            onClick={() => {
              setAnchor(null);
              i.onClick();
            }}
          >
            {i.preview}
            <ListItemText>{i.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function Toolbar({ editor, placeholders }: { editor: Editor; placeholders?: string[] }) {
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      underline: e.isActive('underline'),
      strike: e.isActive('strike'),
      bullet: e.isActive('bulletList'),
      ordered: e.isActive('orderedList'),
      link: e.isActive('link'),
      table: e.isActive('table'),
      left: e.isActive({ textAlign: 'left' }),
      center: e.isActive({ textAlign: 'center' }),
      right: e.isActive({ textAlign: 'right' }),
      block: e.isActive('heading', { level: 1 })
        ? 'Heading 1'
        : e.isActive('heading', { level: 2 })
          ? 'Heading 2'
          : e.isActive('heading', { level: 3 })
            ? 'Heading 3'
            : 'Paragraph',
      undo: e.can().undo(),
      redo: e.can().redo(),
    }),
  });
  const chain = () => editor.chain().focus();
  const [linking, setLinking] = useState<string | null>(null);
  const linkOk = !!linking && /^(https?:\/\/\S+|mailto:\S+|tel:\S+)$/i.test(linking.trim());
  return (
    <Stack
      direction="row"
      role="toolbar"
      aria-label="Formatting"
      sx={{
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.25,
        px: 0.75,
        py: 0.5,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'action.hover',
      }}
    >
      <MenuTool
        title="Text style"
        icon={
          <Typography variant="caption" sx={{ fontWeight: 600, px: 0.5, minWidth: 64 }}>
            {state.block}
          </Typography>
        }
        items={[
          { label: 'Paragraph', onClick: () => chain().setParagraph().run() },
          { label: 'Heading 1', onClick: () => chain().toggleHeading({ level: 1 }).run() },
          { label: 'Heading 2', onClick: () => chain().toggleHeading({ level: 2 }).run() },
          { label: 'Heading 3', onClick: () => chain().toggleHeading({ level: 3 }).run() },
        ]}
      />
      <MenuTool
        title="Text size"
        icon={
          <Typography variant="caption" sx={{ fontWeight: 600, px: 0.5 }}>
            Aa
          </Typography>
        }
        items={[
          { label: 'Normal', onClick: () => chain().unsetFontSize().run() },
          ...SIZES.map((s) => ({ label: s, onClick: () => chain().setFontSize(s).run() })),
        ]}
      />
      <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
      <Tool title="Bold" active={state.bold} onClick={() => chain().toggleBold().run()}>
        <FormatBoldRounded fontSize="small" />
      </Tool>
      <Tool title="Italic" active={state.italic} onClick={() => chain().toggleItalic().run()}>
        <FormatItalicRounded fontSize="small" />
      </Tool>
      <Tool
        title="Underline"
        active={state.underline}
        onClick={() => chain().toggleUnderline().run()}
      >
        <FormatUnderlinedRounded fontSize="small" />
      </Tool>
      <Tool
        title="Strikethrough"
        active={state.strike}
        onClick={() => chain().toggleStrike().run()}
      >
        <FormatStrikethroughRounded fontSize="small" />
      </Tool>
      <MenuTool
        title="Text colour"
        icon={<FormatColorTextRounded fontSize="small" />}
        items={[
          { label: 'Default', onClick: () => chain().unsetColor().run() },
          ...COLOURS.map((c) => ({
            label: c,
            onClick: () => chain().setColor(c).run(),
            preview: (
              <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: c, mr: 1.25 }} />
            ),
          })),
        ]}
      />
      <Tool title="Clear formatting" onClick={() => chain().unsetAllMarks().clearNodes().run()}>
        <FormatClearRounded fontSize="small" />
      </Tool>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
      <Tool
        title="Align left"
        active={state.left}
        onClick={() => chain().setTextAlign('left').run()}
      >
        <FormatAlignLeftRounded fontSize="small" />
      </Tool>
      <Tool
        title="Align centre"
        active={state.center}
        onClick={() => chain().setTextAlign('center').run()}
      >
        <FormatAlignCenterRounded fontSize="small" />
      </Tool>
      <Tool
        title="Align right"
        active={state.right}
        onClick={() => chain().setTextAlign('right').run()}
      >
        <FormatAlignRightRounded fontSize="small" />
      </Tool>
      <Tool
        title="Bulleted list"
        active={state.bullet}
        onClick={() => chain().toggleBulletList().run()}
      >
        <FormatListBulletedRounded fontSize="small" />
      </Tool>
      <Tool
        title="Numbered list"
        active={state.ordered}
        onClick={() => chain().toggleOrderedList().run()}
      >
        <FormatListNumberedRounded fontSize="small" />
      </Tool>
      <Tool
        title="Link"
        active={state.link}
        onClick={() => (state.link ? chain().unsetLink().run() : setLinking('https://'))}
      >
        <LinkRounded fontSize="small" />
      </Tool>
      <MenuTool
        title="Table"
        icon={<TableChartOutlined fontSize="small" />}
        items={[
          {
            label: 'Insert table',
            onClick: () => chain().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(),
          },
          { label: 'Add row', disabled: !state.table, onClick: () => chain().addRowAfter().run() },
          {
            label: 'Add column',
            disabled: !state.table,
            onClick: () => chain().addColumnAfter().run(),
          },
          { label: 'Delete row', disabled: !state.table, onClick: () => chain().deleteRow().run() },
          {
            label: 'Delete column',
            disabled: !state.table,
            onClick: () => chain().deleteColumn().run(),
          },
          {
            label: 'Delete table',
            disabled: !state.table,
            onClick: () => chain().deleteTable().run(),
          },
        ]}
      />
      {placeholders && placeholders.length > 0 && (
        <MenuTool
          title="Insert placeholder"
          icon={
            <Typography variant="caption" sx={{ fontWeight: 600, px: 0.5 }}>
              {'{…}'}
            </Typography>
          }
          items={placeholders.map((p) => ({
            label: p,
            onClick: () => chain().insertContent(p).run(),
          }))}
        />
      )}
      <Box sx={{ flex: 1 }} />
      <Dialog open={linking !== null} onClose={() => setLinking(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Add a link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Link address"
            value={linking ?? ''}
            onChange={(e) => setLinking(e.target.value)}
            helperText="https://…, mailto:… or tel:…"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinking(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!linkOk}
            onClick={() => {
              chain()
                .extendMarkRange('link')
                .setLink({ href: (linking ?? '').trim() })
                .run();
              setLinking(null);
            }}
          >
            Add link
          </Button>
        </DialogActions>
      </Dialog>
      <Tool title="Undo" disabled={!state.undo} onClick={() => chain().undo().run()}>
        <UndoRounded fontSize="small" />
      </Tool>
      <Tool title="Redo" disabled={!state.redo} onClick={() => chain().redo().run()}>
        <RedoRounded fontSize="small" />
      </Tool>
    </Stack>
  );
}

/**
 * A rich-text (HTML) field like Odoo's: headings, sizes, colours, lists, links and tables. The
 * server cleans what it saves and sends; this only edits.
 */
export function RichTextEditor({
  label,
  value,
  onChange,
  disabled = false,
  error = false,
  helperText,
  placeholders,
  minHeight = 160,
}: {
  label: string;
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  /** Placeholders the toolbar can insert, e.g. ['{name}', '{shop}']. */
  placeholders?: string[];
  minHeight?: number;
}) {
  const valueRef = useRef(value);
  useLayoutEffect(() => {
    valueRef.current = value;
  }, [value]);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: { openOnClick: false, autolink: true } }),
      TextStyleKit,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TableKit.configure({ table: { resizable: false } }),
    ],
    content: toEditorHtml(value),
    editable: !disabled,
    immediatelyRender: true,
    editorProps: {
      attributes: { 'aria-label': label, role: 'textbox', 'aria-multiline': 'true' },
    },
    onUpdate: ({ editor: e }) => {
      // Only real edits: the editor also reports its own normalising of an empty value.
      const html = e.getHTML();
      const current = toEditorHtml(valueRef.current);
      if (html === current || (current === '' && html === '<p></p>')) return;
      onChange(html);
    },
  });

  // A new value from outside (a template loaded, a reset) replaces the content.
  useEffect(() => {
    if (editor.isDestroyed) return;
    const next = toEditorHtml(value);
    if (next !== editor.getHTML()) editor.commands.setContent(next, { emitUpdate: false });
  }, [editor, value]);

  useEffect(() => {
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  return (
    <Box>
      <Typography
        variant="caption"
        component="div"
        color={error ? 'error' : 'text.secondary'}
        sx={{ mb: 0.5, fontWeight: 500 }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          border: 1,
          borderColor: error ? 'error.main' : 'divider',
          borderRadius: 1.5,
          overflow: 'hidden',
          bgcolor: disabled ? 'action.disabledBackground' : 'background.paper',
          '&:focus-within': { borderColor: error ? 'error.main' : 'primary.main' },
          '& .tiptap': {
            minHeight,
            maxHeight: 480,
            overflowY: 'auto',
            px: 1.75,
            py: 1.25,
            outline: 'none',
            fontSize: 14,
            lineHeight: 1.55,
            '& p': { my: 0.75 },
            '& h1, & h2, & h3': { mt: 1.25, mb: 0.75, lineHeight: 1.25 },
            '& table': { borderCollapse: 'collapse', my: 1, width: 'auto', tableLayout: 'fixed' },
            '& td, & th': {
              border: '1px solid',
              borderColor: 'divider',
              px: 1,
              py: 0.5,
              minWidth: 60,
              verticalAlign: 'top',
              position: 'relative',
            },
            '& th': { bgcolor: 'action.hover', fontWeight: 600, textAlign: 'left' },
            '& td p, & th p': { my: 0 },
            '& .selectedCell': { bgcolor: 'action.selected' },
            '& a': { color: 'primary.main' },
          },
        }}
      >
        {!disabled && <Toolbar editor={editor} placeholders={placeholders} />}
        <EditorContent editor={editor} />
      </Box>
      {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
    </Box>
  );
}
